/**
 * Per-element index info for an atom inside a labeling scope (typically a feature).
 *
 * `elementIndex` is `undefined` when the scope contains only one atom of this
 * element — that signals to `nameAtom` (and friends) that no `-N` suffix is
 * needed in the label. `totalOfElement` is always populated.
 */
export interface ElementIndexEntry {
    elementIndex?: number;
    totalOfElement: number;
}

/**
 * Compute per-element labeling indices for a sequence of atom symbols within
 * a single scope (e.g. all atoms in a feature).
 *
 * The result is parallel to the input: for each input symbol, the corresponding
 * entry tells you the 1-based position of that atom among atoms of the same
 * element in the scope, and the total count of that element in the scope.
 *
 * Example:
 *   indexElementsInScope(["C", "O", "C", "H", "C", "H"])
 *   // [
 *   //   { elementIndex: 1, totalOfElement: 3 },  // C
 *   //   { elementIndex: undefined, totalOfElement: 1 },  // O (only one)
 *   //   { elementIndex: 2, totalOfElement: 3 },  // C
 *   //   { elementIndex: 1, totalOfElement: 2 },  // H
 *   //   { elementIndex: 3, totalOfElement: 3 },  // C
 *   //   { elementIndex: 2, totalOfElement: 2 },  // H
 *   // ]
 */
export function indexElementsInScope(symbols: string[]): ElementIndexEntry[] {
    // First pass: count total occurrences per element
    const totals = new Map<string, number>();
    for (const s of symbols) {
        totals.set(s, (totals.get(s) ?? 0) + 1);
    }

    // Second pass: assign 1-based indices in input order
    const seen = new Map<string, number>();
    return symbols.map(s => {
        const total = totals.get(s)!;
        if (total <= 1) {
            return {totalOfElement: total};
        }
        const next = (seen.get(s) ?? 0) + 1;
        seen.set(s, next);
        return {elementIndex: next, totalOfElement: total};
    });
}
